import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { SignOut } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { HaleWordmark } from '../HaleBrand';
import TI from '../brand/TI';
import HaleHead from './HaleHead';
import { T, FONT } from '../../pages/hale-console/_theme';

/* HaleConsoleLayout — shared authenticated Hale console shell.
 * Fully Hale-branded chrome (no ToxIntel navigation). Top nav + logout,
 * subtle "powered by" footer. White / Chivo / Hale-red.
 *
 * Nav is role-aware: brand users get their portal nav; admin/operator
 * accounts (e.g. the Hale demo admin@hale.co) get the full Hale operator
 * nav — Dashboard · Applications · Brands · Products · Hale Reviewed ·
 * Documents · Marketplace — all routed to Hale-branded surfaces. */

const BRAND_NAV = [
  { to: '/hale/dashboard',    label: 'Dashboard' },
  { to: '/hale/products',     label: 'My Products' },
  { to: '/hale/applications', label: 'Applications' },
  { to: '/hale/reviews',      label: 'Scientific Review' },
  { to: '/hale/documents',    label: 'Documents' },
  { to: '/hale/profile',      label: 'Profile' },
];

const ADMIN_NAV = [
  { to: '/hale/dashboard',       label: 'Dashboard' },
  { to: '/hale/applications',    label: 'Applications' },
  { to: '/admin/hale-reviewed',  label: 'Brands' },
  { to: '/hale/products',        label: 'Products' },
  { to: '/admin/hale-reviewed',  label: 'Hale Reviewed' },
  { to: '/hale/documents',       label: 'Documents' },
  { to: '/hale/marketplace',     label: 'Marketplace' },
];

export default function HaleConsoleLayout({ children }) {
  const { logout, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const NAV = isAdmin ? ADMIN_NAV : BRAND_NAV;

  const handleLogout = async () => {
    await logout();
    navigate('/hale/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: T.NEUTRAL, color: T.INK, fontFamily: FONT.BODY }}>
      <HaleHead title="Hale Brand Portal" />
      {/* Top bar */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(10px)', borderBottom: `1px solid ${T.LINE}` }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-4">
          <Link to="/hale/dashboard" aria-label="Hale console home" className="flex-shrink-0" data-testid="hale-console-brand">
            <HaleWordmark size="sm" />
          </Link>

          <nav className="flex-1 flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="hale-console-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                end={n.to === '/admin/hale-reviewed'}
                data-testid={`hale-nav-${n.label.toLowerCase().split(' ')[0]}`}
                className="px-3 py-2 rounded-md text-[12.5px] font-medium whitespace-nowrap transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? T.INK : T.MUTED,
                  backgroundColor: isActive ? 'rgba(230,57,70,0.08)' : 'transparent',
                })}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <button
            onClick={handleLogout}
            data-testid="hale-logout-btn"
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12px] font-semibold border transition-colors"
            style={{ borderColor: T.LINE, color: T.INK, backgroundColor: T.WHITE }}
          >
            <SignOut size={14} weight="bold" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-5 sm:px-8 py-10 lg:py-12">
        {user && (
          <p className="text-[11px] mb-6" style={{ color: T.MUTED }} data-testid="hale-console-whoami">
            Signed in as <span className="font-semibold" style={{ color: T.INK }}>{user.email}</span>
            {user.company ? <> · {user.company}</> : null}
          </p>
        )}
        {children}
      </main>

      <footer style={{ borderTop: `1px solid ${T.LINE}`, backgroundColor: T.WHITE }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>The Hale League</span>
          <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>
            Scientific review powered by <TI color={T.PINK} /> + Aᴳ
          </span>
        </div>
      </footer>
    </div>
  );
}
